import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  ContributorFilter as ContributorFilterModel,
  ContributorListDto,
  ContributorListFilter,
  ContributorListResource,
  Permission,
  PermissionAction,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { contributorCreatePageRoute } from "../ContributorCreatePage/ContributorCreatePage";
import { ContributorFilter } from "../ContributorFilter";
import { ContributorTable } from "./ContributorTable";

const routeName = "contributors";

export const contributorListPageRoute: PageRoute = {
  handle: "Contributors",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const ContributorListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Contributor;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<ContributorListFilter>({
      sortObjects: [{ property: pn<ContributorListDto>("fullName"), isDesc: false }],
      contributorFilter: {
          active: true,
      },
  });
  const [initListFilter] = useState<ContributorListFilter>(listFilter);
  const [listItems, setListItems] = useState<ContributorListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<ContributorListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.contributors.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.contributors.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.contributors.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<ContributorListFilter>({
    maxWidth: "md",
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <ContributorFilter
        filter={listFilter.contributorFilter || ({} as ContributorFilterModel)}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.contributorFilter = filter;
            })
          )
        }
      />
    ),
  });

  const getIdsMethods = () => ApiService.contributors.getIds(listFilter);

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{contributorListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button onClick={() => navigate(contributorCreatePageRoute.getPath())} variant="outlined">
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <ContributorTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              contributors={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
